/**
 * OperatorFilter plugin.
 *
 * Filters grant calls by operator (institution) IDs.
 * URL param: operatorId (singular), API/SavedView: operatorIds (plural).
 *
 * Fetches operator list via React Query when rendered.
 *
 * Faceted counts (AC-6): when `facets.operatorCounts` is provided (articles
 * page), each option shows the count of matching results. When absent (calls
 * page, which does not supply operator facets), options render without counts —
 * additive and graceful, identical to the previous behaviour.
 *
 * @see docs/specifications/2026-03-10-plugin-filter-architecture.md (Section 4)
 */

import React, { useMemo } from "react";
import { FilterPlugin, FilterComponentProps } from "../types";
import { SavedViewFilters, ArticleFacetCount } from "../../../../../services/grantsApi";
import MultiSelectDropdown from "../../../../common/MultiSelectDropdown";
import { useQuery } from "@tanstack/react-query";
import apiClient from "../../../../../services/api";

/**
 * Operator filter React component.
 * Fetches operators from API and renders a multi-select dropdown.
 */
const OperatorFilterComponent = React.memo(function OperatorFilterComponent(
  props: FilterComponentProps<string[]>
) {
  const { value, onChange, facets } = props;

  const { data: operatorsData } = useQuery({
    queryKey: ["grants-operators"],
    queryFn: async () => {
      const { data } = await apiClient.get("/grants/operators");
      return data;
    },
  });

  const operatorCounts = facets?.operatorCounts as
    | ArticleFacetCount[]
    | undefined;

  const options = useMemo(
    () =>
      ((operatorsData?.operators as Array<{ id: string; name: string; shortName?: string; region?: string }>) ?? []).map(
        (op) => ({
          value: op.id,
          label: op.shortName || op.name + (op.region ? ` (${op.region})` : ""),
          // Faceted count (articles page): when facets are supplied, every
          // option shows a count — 0 for operators with no matches under the
          // current filters (faceted-search convention: surface alternatives).
          // When facets are absent (calls page), count stays undefined and
          // MultiSelectDropdown renders no "(N)" — identical to prior behaviour.
          count: operatorCounts
            ? (operatorCounts.find((c) => c.id === op.id)?.count ?? 0)
            : undefined,
        })
      ),
    [operatorsData?.operators, operatorCounts]
  );

  return React.createElement("div", null,
    React.createElement("label", {
      className: "block text-sm font-medium text-neutral-700 mb-1",
    }, "Operator"),
    React.createElement(MultiSelectDropdown, {
      options,
      selectedValues: value,
      onChange,
      placeholder: "Wszystkie",
      ariaLabel: "Filtruj według operatora",
    }),
    React.createElement("p", {
      className: "mt-1 text-xs text-neutral-500",
    }, "Filtruj nabory według instytucji wdrażającej")
  );
});

export const OperatorFilterPlugin: FilterPlugin<string[]> = {
  key: "operator",
  label: "Operator",
  placement: "advanced",
  order: 1,
  defaultValue: [],
  Component: OperatorFilterComponent,

  isActive: (value: string[]): boolean => value.length > 0,

  toUrlParams: (value: string[], params: URLSearchParams): void => {
    value.forEach((id) => params.append("operatorId", id));
  },

  fromUrlParams: (params: URLSearchParams): string[] => {
    return params.getAll("operatorId");
  },

  toSavedView: (
    value: string[],
    filters: Partial<SavedViewFilters>
  ): void => {
    filters.operatorIds = value;
  },

  fromSavedView: (filters: SavedViewFilters): string[] => {
    return filters.operatorIds ?? [];
  },

  toApiParams: (value: string[], params: URLSearchParams): void => {
    value.forEach((id) => params.append("operatorIds", id));
  },
};
